import { EmptyState, LoadingState } from '@components/common';
import { useAppSelector } from '@hooks/useRedux';
import { selectCurrentUser } from '@store/auth/authSelectors';
import {
  selectAllOutlets,
  selectEffectiveOutlet,
  selectEffectiveOutletId,
} from '@store/outlets';
import React, { useEffect, useMemo, useState } from 'react';
import {
  fetchDashboardDepartments,
  fetchDashboardPayments,
  fetchDashboardRecent,
  fetchDashboardSummary,
} from '../../services/dashboardService';
import { DashboardOverview } from '../../types/dashboard';
import { CrossOutletCard } from './CrossOutletCard';
import { DashboardContext } from './DashboardContext';
import { DashboardHeader } from './DashboardHeader';
import { DepartmentBarChartCard } from './DepartmentBarChartCard';
import { getLatestBusinessDate, getRangeStart } from './helpers';
import { MetricsRow } from './MetricsRow';
import { NetSalesCard } from './NetSalesCard';
import { QuickStatsCard } from './QuickStatsCard';
import { RecentOrdersCard } from './RecentOrdersCard';
import { SmartInsightCard } from './SmartInsightCard';
import { SpendingOverviewCard } from './SpendingOverviewCard';
import { TrendAreaChartCard } from './TrendAreaChartCard';

export const DashboardHome: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const selectedOutletId = useAppSelector(selectEffectiveOutletId);
  const selectedOutlet = useAppSelector(selectEffectiveOutlet);
  const outlets = useAppSelector(selectAllOutlets);

  const [dashboard, setDashboard] = useState<DashboardOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<number>(30);
  const [loadingStates, setLoadingStates] = useState({
    summary: true,
    recent: true,
    payments: true,
    departments: true,
  });

  const scopeLabel = selectedOutlet ? selectedOutlet.name : 'All Outlets';
  const outletCountInScope = selectedOutlet ? 1 : outlets.length;

  const endDate = useMemo(
    () =>
      getLatestBusinessDate(
        selectedOutlet?.day_open,
        outlets.map((outlet) => outlet.day_open),
      ),
    [outlets, selectedOutlet?.day_open],
  );

  const startDate = useMemo(
    () => getRangeStart(endDate, period),
    [endDate, period],
  );

  useEffect(() => {
    let isActive = true;

    const loadDashboard = async () => {
      if (!currentUser?.company?.id) {
        if (!isActive) return;
        setDashboard(null);
        setError('Company context is missing for the current user.');
        return;
      }

      // Initialize skeleton layout immediately
      const params = {
        companyId: currentUser.company.id,
        outletId: selectedOutletId,
        startDate,
        endDate,
      };

      setDashboard({
        startDate,
        endDate,
        summary: {
          grossSales: 0,
          netSales: 0,
          totalDiscounts: 0,
          billsSettled: 0,
          settlementCount: 0,
          averageBill: 0,
          creditIssued: 0,
          ncIssued: 0,
        },
        trend: [],
        paymentBreakdown: [],
        departmentPerformance: [],
        recentOrders: [],
        outletPerformance: [],
      });
      setLoadingStates({
        summary: true,
        recent: true,
        payments: true,
        departments: true,
      });

      // Sequentially fetch chunks to prevent PostgreSQL statement timeouts from concurrent heavy aggregations
      const recentRes = await fetchDashboardRecent(params);
      if (!isActive) return;
      if (recentRes.success && recentRes.data) {
        setDashboard((curr) => (curr ? { ...curr, ...recentRes.data! } : null));
      }
      setLoadingStates((curr) => ({ ...curr, recent: false }));

      const summaryRes = await fetchDashboardSummary(params);
      if (!isActive) return;
      if (summaryRes.success && summaryRes.data) {
        setDashboard((curr) =>
          curr ? { ...curr, ...summaryRes.data! } : null,
        );
      } else if (!recentRes.success) {
        setError(summaryRes.error || 'Failed to load dashboard overview');
      }
      setLoadingStates((curr) => ({ ...curr, summary: false }));

      const paymentsRes = await fetchDashboardPayments(params);
      if (!isActive) return;
      if (paymentsRes.success && paymentsRes.data) {
        setDashboard((curr) =>
          curr ? { ...curr, ...paymentsRes.data! } : null,
        );
      }
      setLoadingStates((curr) => ({ ...curr, payments: false }));

      const departmentsRes = await fetchDashboardDepartments(params);
      if (!isActive) return;
      if (departmentsRes.success && departmentsRes.data) {
        setDashboard((curr) =>
          curr ? { ...curr, ...departmentsRes.data! } : null,
        );
      }
      setLoadingStates((curr) => ({ ...curr, departments: false }));
    };

    loadDashboard();

    return () => {
      isActive = false;
    };
  }, [currentUser?.company?.id, endDate, selectedOutletId, startDate, period]);

  const hasData = useMemo(() => {
    if (!dashboard) return false;
    return (
      dashboard.summary.netSales > 0 ||
      dashboard.summary.grossSales > 0 ||
      dashboard.recentOrders.length > 0 ||
      dashboard.outletPerformance.length > 0
    );
  }, [dashboard]);

  const isFullyLoaded =
    !loadingStates.summary &&
    !loadingStates.recent &&
    !loadingStates.payments &&
    !loadingStates.departments;

  const contextValue = useMemo(
    () => ({
      dashboard: dashboard!, // Safely assert non-null because we don't render context providers when null
      selectedOutletId,
      scopeLabel,
      outletCountInScope,
      period,
      setPeriod,
      loadingStates,
    }),
    [
      dashboard,
      selectedOutletId,
      scopeLabel,
      outletCountInScope,
      period,
      setPeriod,
      loadingStates,
    ],
  );

  if (error) {
    return (
      <div className="rounded-[22px] border border-rose-200 bg-rose-50 px-6 py-5 text-sm text-rose-700">
        {error}
      </div>
    );
  }

  if (!dashboard) {
    return (
      <LoadingState message="Initializing dashboard layout..." size="lg" />
    );
  }

  if (isFullyLoaded && !hasData) {
    return (
      <EmptyState
        title="No dashboard data yet"
        description="You do not have any sales data for the past 30 days. Once you start making sales, your dashboard will come to life with insights and trends!"
      />
    );
  }

  return (
    <DashboardContext.Provider value={contextValue}>
      <div className="mx-auto w-full max-w-[1400px] px-2 pb-8 flex flex-col gap-4">
        {/* -- Header -- */}
        <DashboardHeader />

        {/* -- Row 1: Hero Net Sales + 3 KPI Cards -- */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-5">
          <NetSalesCard />
          <MetricsRow />
        </div>

        {/* -- Row 2: Trend Chart + Donut + Insight + Quick Stats -- */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
          <TrendAreaChartCard />
          <SpendingOverviewCard />
          <QuickStatsCard />
        </div>

        {/* -- Row 3: Department Bar Chart + Settlements + Orders -- */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-5 h-[300px] overflow-hidden">
          <DepartmentBarChartCard />
          <RecentOrdersCard />
          <SmartInsightCard />
        </div>

        {/* -- Row 4: Cross-outlet (conditional) -- */}
        <div className="grid grid-cols-1 gap-4">
          <CrossOutletCard />
        </div>
      </div>
    </DashboardContext.Provider>
  );
};
